import {
  Button,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  Switch,
  Text,
} from '@chakra-ui/react'
import { useState } from 'react'
import CustomTabs from '../input/CustomTabs'
import CustomTabsMultiple from '../input/CustomTabsMultiple'
import CustomTabsString from '../input/CustomTabsString'

const CommunityFilterModal = ({
  isOpen,
  onClose,
  onApply,
  initialFilters,
  hideDistance = false,
}: {
  isOpen: boolean
  onClose: () => void
  onApply: (filters: any) => void
  initialFilters?: {
    gender: string
    age_range: string
    skill_level: string
    offers_private_lessons: boolean | null
    range: number | null
  }
  hideDistance?: boolean
}) => {
  const [filters, setFilters] = useState(
    initialFilters || {
      gender: '',
      age_range: '',
      skill_level: '',
      offers_private_lessons: null,
      range: null,
    }
  )

  const handleApply = () => {
    onApply(filters)
    onClose()
  }

  const handleReset = () => {
    setFilters({
      gender: '',
      age_range: '',
      skill_level: '',
      offers_private_lessons: null,
      range: null,
    })
    onApply({
      gender: '',
      range: null,
      age_range: '',
      skill_level: '',
      offers_private_lessons: null,
    })
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalContent>
        <ModalHeader fontSize="22px" color="#171C22" p="0" lineHeight="28px">
          Filter
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p="0">
          <Flex alignItems="center" justifyContent="space-between" my="15px">
            <Text variant="title" fontSize="md">
              Looking for a coach
            </Text>
            <Switch
              isChecked={filters.offers_private_lessons === true}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  offers_private_lessons: e.target.checked ? true : null,
                })
              }
              colorScheme="green"
              size="lg"
            />
          </Flex>
          <Divider mb="20px" />

          <Text variant="title" fontSize="md" mb="5px">
            Gender
          </Text>
          <Flex gap="10px" flexWrap="wrap">
            <CustomTabsString
              name="gender"
              value={filters.gender}
              onChange={(v: string) => setFilters({ ...filters, gender: v })}
              choices={['All', 'Female', 'Male', 'Other']}
              results={['', 'F', 'M', 'X']}
            />
          </Flex>
          <Divider mt="10px" mb="20px" />

          <Text variant="title" fontSize="md" mb="5px">
            Age Range
          </Text>
          <Flex gap="10px" flexWrap="wrap">
            <CustomTabs
              name="age_range"
              value={
                filters.age_range === '18'
                  ? 1
                  : filters.age_range === '30'
                  ? 2
                  : filters.age_range === '40'
                  ? 3
                  : filters.age_range === '50'
                  ? 4
                  : 0
              }
              onChange={(v: number) =>
                setFilters({
                  ...filters,
                  age_range: v === 0 ? '' : v === 1 ? '18' : v === 2 ? '30' : v === 3 ? '40' : v === 4 ? '50' : '',
                })
              }
              choices={['All', '18-29', '30-39', '40-49', '50+']}
            />
          </Flex>
          <Divider mt="10px" mb="20px" />

          <Text variant="title" fontSize="md" mb="5px">
            Skill Level
          </Text>
          <Flex gap="10px" flexWrap="wrap">
            <CustomTabsMultiple
              value={filters.skill_level}
              onChange={(v: string) => setFilters({ ...filters, skill_level: v })}
              choices={['Beginner', 'Intermediate', 'Intermediate High', 'Advanced', 'Advanced 4.5+', 'Mixed-Level']}
              results={['1', '2', '3', '4', '5', '6']}
            />
          </Flex>

          <Divider mt="10px" mb="20px" />

          {!hideDistance && (
            <>
          <Text variant="title" fontSize="md" mb="5px">
            Distance filter
          </Text>

          <Text variant="subtle" fontSize="sm" fontWeight="400">
            Set the maximum search distance{' '}
            <span style={{ color: '#171C22' }}>
              1mi - {filters?.range === 100 || filters?.range === null ? '∞' : `${filters?.range}mi`}
            </span>
          </Text>
          <Flex mt="5px" justifyContent="center">
            <Slider
              name="range"
              w="85%"
              aria-label="slider-ex-1"
              mr="16px"
              min={1}
              max={100}
              value={filters.range ?? 100}
              onChange={(num) => setFilters({ ...filters, range: num === 100 ? null : num })}
            >
              <SliderMark value={1} mt="8px">
                <Text variant="subtle" fontSize="sm">
                  1 mi
                </Text>
              </SliderMark>
              <SliderMark value={20} mt="8px">
                <Text variant="subtle" fontSize="sm">
                  20 mi
                </Text>
              </SliderMark>
              <SliderMark value={40} mt="8px">
                <Text variant="subtle" fontSize="sm">
                  40 mi
                </Text>
              </SliderMark>
              <SliderMark value={60} mt="8px">
                <Text variant="subtle" fontSize="sm">
                  60 mi
                </Text>
              </SliderMark>
              <SliderMark value={80} mt="8px">
                <Text variant="subtle" fontSize="sm">
                  80 mi
                </Text>
              </SliderMark>

              <SliderTrack bg="#F5F7F9">
                <SliderFilledTrack bg="#171C22" />
              </SliderTrack>
              <SliderThumb bg="#171C22" w="19px" h="20px" />
            </Slider>
            <Text fontSize="md" pl="5px" w="15%">
              {filters?.range === null ? '∞' : `${filters?.range}mi`}
            </Text>
          </Flex>
          <Divider mt="30px" mb="20px" />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Flex w="100%" justifyContent="space-between">
            <Button variant="ghost" onClick={handleReset}>
              Reset
            </Button>
            <Button variant="heavy" onClick={handleApply}>
              Apply
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default CommunityFilterModal
